import React, { useState, useEffect } from 'react';
import { FaVolumeUp, FaVolumeOff } from 'react-icons/fa';
import { playSmartAudio } from '../utils/audioUtils';
import { checkAudioExists, AudioCheckResult } from '../utils/audioFileChecker';

interface AudioButtonProps {
  audioUrl?: string;
  text: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary' | 'accent';
  disabled?: boolean;
  onPlayStart?: () => void;
  onPlayEnd?: () => void;
  onError?: (error: Error) => void;
}

const AudioButton: React.FC<AudioButtonProps> = ({
  audioUrl,
  text,
  className = '',
  size = 'md',
  variant = 'primary',
  disabled = false,
  onPlayStart,
  onPlayEnd,
  onError
}) => {
  const [audioResult, setAudioResult] = useState<AudioCheckResult | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  // Check audio availability when component mounts or audioUrl changes
  useEffect(() => {
    if (!audioUrl) {
      setAudioResult(null);
      return;
    }

    setIsChecking(true);
    checkAudioExists(audioUrl)
      .then(result => {
        setAudioResult(result);
      })
      .catch(error => {
        console.error('Error checking audio availability:', error);
        setAudioResult({
          exists: false,
          isAudio: false,
          error: error.message
        });
      })
      .finally(() => {
        setIsChecking(false);
      });
  }, [audioUrl]);

  const isAvailable = audioResult ? audioResult.exists && audioResult.isAudio : false;
  const isLoading = isChecking || (audioResult === null && audioUrl);

  const handleClick = async () => {
    if (!isAvailable || !audioUrl || disabled || isPlaying) return;

    try {
      setIsPlaying(true);
      if (onPlayStart) onPlayStart();
      
      await playSmartAudio(audioUrl, text);
      
      if (onPlayEnd) onPlayEnd();
    } catch (error) {
      console.error('Error playing audio:', error);
      if (onError) onError(error as Error);
    } finally {
      setIsPlaying(false);
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm': return 'p-1 text-sm';
      case 'lg': return 'p-3 text-lg';
      default: return 'p-2 text-base';
    }
  };

  const getVariantClasses = () => {
    const baseClasses = 'rounded-lg transition-all duration-200 hover:scale-110';
    
    if (disabled || !isAvailable || isLoading) {
      return `${baseClasses} text-gray-400 cursor-not-allowed`;
    }

    switch (variant) {
      case 'secondary':
        return `${baseClasses} text-gray-600 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-700`;
      case 'accent':
        return `${baseClasses} text-pink-500 hover:text-pink-600 hover:bg-pink-50 dark:hover:bg-pink-900/20`;
      default: // primary
        return `${baseClasses} text-purple-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20`;
    }
  };

  const getTooltipMessage = () => {
    if (isLoading) return 'Checking audio availability...';
    if (isPlaying) return 'Playing audio...';
    if (disabled) return 'Audio disabled';
    if (!audioUrl) return 'No audio URL provided';
    if (!audioResult) return 'Audio availability unknown';
    if (isAvailable) return `Play audio for ${text}`;
    return audioResult.error || `No audio available for ${text}`;
  };

  const getIcon = () => {
    if (isLoading) {
      return (
        <div className="animate-spin rounded-full border-b-2 border-current" 
             style={{ width: '16px', height: '16px' }}></div>
      );
    }
    
    if (isPlaying) {
      return (
        <div className="animate-pulse">
          <FaVolumeUp />
        </div>
      );
    }
    
    return isAvailable ? <FaVolumeUp /> : <FaVolumeOff />;
  };

  return (
    <button
      onClick={handleClick}
      disabled={Boolean(disabled) || !Boolean(isAvailable) || Boolean(isLoading) || Boolean(isPlaying)}
      className={`${getSizeClasses()} ${getVariantClasses()} ${className}`}
      title={getTooltipMessage()}
      aria-label={getTooltipMessage()}
    >
      {getIcon()}
    </button>
  );
};

export default AudioButton;
